<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import type { NoticeContent } from "$lib/elev8Api/contentTypes";
  import { t } from "$lib/utils/i18n";
  import { getConfigurationContext } from "$lib/elev8Api/configurationContext.svelte";
  import { recordPlayEvent } from "$lib/dataPipeline/dataPipelineManager";

  interface Props {
    content: NoticeContent;
  }

  /**
   * //NOTE: Character limits (max, recommended): title = 109, 21; body = 1053, 255; big vs side threshold = 145
   */

  let { content: _content }: Props = $props();
  const content = $derived(_content);
  // Pinned at script-init: onDestroy runs after the outro, when props already
  // point at the next spot's content.
  // svelte-ignore state_referenced_locally
  const playedContent = _content;
  const config = getConfigurationContext();
  const hasText = $derived(!!content.title_text || !!content.body_text);
  const overlay = $derived(!!content.asset_url && hasText);

  let imageLoaded = false;
  let imgElement: HTMLImageElement | undefined = $state();

  onMount(() => {
    if (imgElement?.complete && imgElement.naturalWidth > 0) {
      imageLoaded = true;
    }
  });

  function onload(event: Event) {
    const img = event.currentTarget as HTMLImageElement;
    if (img.complete && img.naturalWidth > 0) {
      imageLoaded = true;
    }
  }

  onDestroy(() => {
    const imageDisplayed =
      imageLoaded ||
      (imgElement?.complete === true && imgElement.naturalWidth > 0);
    const noticeDisplayed =
      Boolean(playedContent.title_text || playedContent.body_text) ||
      imageDisplayed;

    if (config.screen?.vision_enabled && noticeDisplayed) {
      recordPlayEvent(playedContent);
    }
  });

  function imgError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.onerror = null; // prevent looping
    img.style.display = "none";
    //TODO errorReporter?? Graceful fallback? single pixel gif uri? (src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=")
  }
</script>

<div class="vc-content-layout notice-layout" class:notice-layout--overlay={overlay}>
  {#if content.asset_url}
    <img
      src={content.asset_url}
      class:vc-content-media={!overlay}
      class:vc-content-background-image={overlay}
      alt=""
      crossorigin="anonymous"
      bind:this={imgElement}
      {onload}
      onerror={imgError}
    />
  {/if}

  {#if hasText}
    <div class="title-wrap-for-line-clamp">
      <h1 class="title">
        {content.title_text || t("notice.fallbackTitle", config.language)}
      </h1>
    </div>
  {/if}
  {#if content.body_text}
    <div class="content-wrap-for-line-clamp">
      <p class="content-body">{content.body_text}</p>
    </div>
  {/if}
</div>

<style>
  /* ============================================
   * Component Tokens (customize via CSS variables)
   * 
   * Available for override by parent layouts:
   * --notice-border-color: sets the border color of the content box (default: var(--vc-neutral-light-20))
   * --notice-content-border: sets the bottom border of the content box, allowing for thickness
   * 
   * Note: All text colors and fonts inherit from parent layout unless overridden via tokens.
   * ============================================ */
  .notice-layout {
    background: var(--vc-white);
    /* background: linear-gradient(
      to bottom,
      var(--vc-primary-80),
      var(--vc-primary)
    ); */
    display: flex;
    justify-content: flex-start;
  }
  /* Image + text: image fills the frame, text sits over it (same treatment as RssFeedContent) */
  .notice-layout--overlay {
    justify-content: flex-end;
  }
  /* :global() on descendants: Svelte would otherwise scope them with :where(),
     which Chrome 49 and WPE cannot apply. */
  .notice-layout--overlay :global(.title-wrap-for-line-clamp),
  .notice-layout--overlay :global(.content-wrap-for-line-clamp) {
    position: relative;
    z-index: 10;
  }
  .notice-layout--overlay :global(.title-wrap-for-line-clamp) {
    background-color: var(--vc-primary-80);
  }
  .notice-layout--overlay :global(.content-wrap-for-line-clamp) {
    flex: 0 0 auto;
    background-color: var(--vc-white-80);
    backdrop-filter: blur(1em);
  }

  .title-wrap-for-line-clamp {
    /* font-size: clamp(4rem, 6em, 10rem); */
    font-size: calc(5rem * var(--layout-font-scale, 1));
    flex: 0 0 auto;
    padding: 0.2em 1em;
    margin: 0;
    background-color: var(--vc-primary);
  }

  .title {
    font-family: var(--vc-font-h1);
    font-weight: 800;
    text-transform: capitalize;
    letter-spacing: -0.01em;
    color: var(--vc-white);
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .content-wrap-for-line-clamp {
    font-size: calc(3.1rem * var(--layout-font-scale, 1));
    flex: 1 0 5em;
    padding: 1em 0.7em;
    /* margin: 3.2rem 3.2rem 4rem; */
    margin: 0;
    background-color: var(--vc-white);
    border: var(
      --notice-content-border,
      1px solid var(--notice-border-color, var(--vc-neutral-light-20))
    );
  }

  .content-body {
    font-family: var(--vc-font-body);
    font-weight: 700;
    line-height: 1.5;
    overflow-wrap: break-word;
    hyphens: auto;
    color: var(--vc-black);
    display: -webkit-box;
    -webkit-line-clamp: 6;
    line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-aspect-ratio: 16/10) {
    /* .content-body {
      -webkit-line-clamp: 7;
      line-clamp: 7;
    } */
  }
</style>
